import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CreateDefaultExemptionParkingTypeRequest, ParkingTypeService, ParkingSpaceDto, ParkingZoneDto } from '../../../../../api/gen';

export interface DefaultExemptionResult {
  success: boolean;
  data?: any;
}

@Component({
  selector: 'app-add-default-exemption',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatSnackBarModule
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './add-default-exemption.component.html',
  styleUrl: './add-default-exemption.component.scss'
})
export class AddDefaultExemptionComponent implements OnInit {
  private fb = inject(FormBuilder);
  private parkingTypeService = inject(ParkingTypeService);
  private snackBar = inject(MatSnackBar);

  @Input() parkingSpace!: ParkingSpaceDto;
  @Input({ required: true }) parkingOperatorId: string | undefined;

  @Output() submitted = new EventEmitter<DefaultExemptionResult>();

  form!: FormGroup;
  isLoading = signal(false);
  availableZones = signal<ParkingZoneDto[]>([]);

  ngOnInit(): void {
    this.initForm();
    this.availableZones.set(this.parkingSpace?.parkingZones ?? []);
  }

  private initForm(): void {
    this.form = this.fb.group({
      licensePlatesRegex: [''],
      availableForParkingZones: [[] as string[]],
      availableFrom: [null],
      availableTo: [null],
      skipProcessRecording: [false]
    });
  }

  submit(): void {
    if (this.form.invalid) return;

    this.isLoading.set(true);

    const formValue = this.form.value;
    const request: CreateDefaultExemptionParkingTypeRequest = {
      licensePlatesRegex: formValue.licensePlatesRegex || null,
      avaliableFrom: formValue.availableFrom ? new Date(formValue.availableFrom).toISOString() : null,
      avaliableTo: formValue.availableTo ? new Date(formValue.availableTo).toISOString() : null,
      availableForParkingSpaces: this.parkingSpace?.id ? [this.parkingSpace.id] : null,
      // Empty means the whole space; sent as an empty list, like the short-term form.
      availableForParkingZones: formValue.availableForParkingZones ?? [],
      organizationId: null,
      skipProcessRecording: formValue.skipProcessRecording ?? false
    };

    this.parkingTypeService.parkingTypesDefaultExemptionsPost(this.parkingOperatorId, request).subscribe({
      next: (result) => {
        this.snackBar.open('Default exemption created successfully', 'Close', { duration: 3000 });
        this.isLoading.set(false);
        this.submitted.emit({ success: true, data: result });
      },
      error: (error) => {
        console.error('Error creating default exemption:', error);
        this.snackBar.open('Failed to create default exemption', 'Close', { duration: 3000 });
        this.isLoading.set(false);
        this.submitted.emit({ success: false });
      }
    });
  }
}
